import React from 'react';
import {
  AbsoluteFill,
  Img,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
} from 'remotion';
import type { KenBurnsConfig } from '../types';
import { resolveMediaSrc } from '../media';

interface SceneBackgroundProps {
  imagePath: string;
  kenBurns: KenBurnsConfig;
  durationInFrames: number;
  overlayOpacity?: number;
  overlayColor?: string;
}

/**
 * Background image component with Ken Burns effect (slow pan/zoom)
 * Makes static images feel cinematic and dynamic
 */
export const SceneBackground: React.FC<SceneBackgroundProps> = ({
  imagePath,
  kenBurns,
  durationInFrames,
  overlayOpacity = 0.5,
  overlayColor = '#000000',
}) => {
  const frame = useCurrentFrame();

  // Minimum scale to prevent edge exposure during Ken Burns movement
  const MIN_SCALE = 1.15;

  // Interpolate Ken Burns values over the scene duration
  const rawScale = interpolate(
    frame,
    [0, durationInFrames],
    [kenBurns.startScale, kenBurns.endScale],
    { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
  );

  // Enforce minimum scale to always overfill the frame
  const scale = Math.max(rawScale, MIN_SCALE);

  // Calculate max safe translation based on current scale
  // At scale 1.15, we can translate up to ~7.5% before showing edges
  const maxTranslate = ((scale - 1) / 2) * 100 * 0.8; // 80% safety margin

  const rawTranslateX = interpolate(
    frame,
    [0, durationInFrames],
    [kenBurns.startX - 50, kenBurns.endX - 50], // Convert from % to translation
    { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
  );

  const rawTranslateY = interpolate(
    frame,
    [0, durationInFrames],
    [kenBurns.startY - 50, kenBurns.endY - 50],
    { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
  );

  // Clamp translations to prevent edge exposure
  const translateX = Math.max(-maxTranslate, Math.min(maxTranslate, rawTranslateX));
  const translateY = Math.max(-maxTranslate, Math.min(maxTranslate, rawTranslateY));

  // Rotation support (optional)
  const rotation = interpolate(
    frame,
    [0, durationInFrames],
    [kenBurns.startRotation ?? 0, kenBurns.endRotation ?? 0],
    { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
  );

  return (
    <AbsoluteFill>
      {/* Dark fallback background to prevent alpha/transparency gaps */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backgroundColor: '#0a0a12',
        }}
      />

      {/* Image container with overflow hidden for Ken Burns */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Img
          src={resolveMediaSrc(imagePath)}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: `scale(${scale}) translate(${translateX}%, ${translateY}%) rotate(${rotation}deg)`,
            transformOrigin: 'center center',
          }}
        />
      </div>

      {/* Dark overlay for text readability */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backgroundColor: overlayColor,
          opacity: overlayOpacity,
        }}
      />

      {/* Optional vignette effect */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.4) 100%)',
        }}
      />
    </AbsoluteFill>
  );
};

interface ReportSlidesBackgroundProps {
  imagePaths: string[];
  durationInFrames: number;
  overlayOpacity?: number;
  overlayColor?: string;
}

/**
 * Slideshow background for the market report slides
 * Straight cuts between slides (no crossfade/zoom).
 */
export const ReportSlidesBackground: React.FC<ReportSlidesBackgroundProps> = ({
  imagePaths,
  durationInFrames,
  overlayOpacity = 0.35,
  overlayColor = '#000000',
}) => {
  const frame = useCurrentFrame();

  if (imagePaths.length === 0) return null;

  const slideCount = imagePaths.length;
  const segmentFrames = durationInFrames / slideCount;
  const currentIndex = Math.min(slideCount - 1, Math.floor(frame / segmentFrames));
  const currentImage = imagePaths[currentIndex];

  return (
    <AbsoluteFill>
      {/* Dark fallback background to prevent alpha/transparency gaps */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backgroundColor: '#0a0a12',
        }}
      />

      {/* Slides - straight cuts */}
      <AbsoluteFill>
        <Img
          src={resolveMediaSrc(currentImage)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </AbsoluteFill>

      {/* Dark overlay for text readability */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backgroundColor: overlayColor,
          opacity: overlayOpacity,
        }}
      />

      {/* Subtle vignette */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.35) 100%)',
        }}
      />
    </AbsoluteFill>
  );
};

/**
 * Fallback gradient background when image is not available
 */
interface GradientBackgroundProps {
  primaryColor: string;
  secondaryColor?: string;
}

export const GradientBackground: React.FC<GradientBackgroundProps> = ({
  primaryColor,
  secondaryColor = '#0a0a12',
}) => {
  const frame = useCurrentFrame();
  const { durationInFrames } = useVideoConfig();

  // Subtle gradient animation
  const gradientPosition = interpolate(
    frame,
    [0, durationInFrames],
    [0, 360],
    { extrapolateRight: 'clamp' }
  );

  const x = 50 + Math.sin(gradientPosition * 0.02) * 20;
  const y = 50 + Math.cos(gradientPosition * 0.015) * 15;

  return (
    <AbsoluteFill
      style={{
        background: `
          radial-gradient(ellipse at ${x}% ${y}%, ${primaryColor}40 0%, transparent 60%),
          linear-gradient(180deg, ${secondaryColor} 0%, #000000 100%)
        `,
      }}
    />
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
